import { To } from 'react-router-dom'
import { toRelativeUrl } from '@okta/okta-auth-js'
import { IMkAuthenticatedSession, IMkAuthorizedSession, TMkSession, TMkAuthenticationState } from '@auth/types'
import { DataStore, EMkStorageKeys } from '@utils/DataStore'
import { ProtectedDataStore } from '@utils/ProtectedDataStore'
import { OktaAuthnResponse } from '@hooks/useOkta'
import { toRouterPath } from '@utils/basePath'
import { routeLogger } from '@utils/routeLogger'

interface ILockoutState {
  isAccountLocked: boolean
  lockoutEndTime: number | null
  lockedUsername: string | null
}

export interface IErrorCallbackArgs {
  error: string
  errorDescription?: string
  brand?: string
}

export abstract class IAuthenticationService {
  public static initialState = () => {
    const makoSession = ProtectedDataStore.getItem<TMkSession>(EMkStorageKeys.MAKO_SESSION)
    const loginOrigin = DataStore.getItem<string>(EMkStorageKeys.LOGIN_ORIGIN)
    const isAuthenticated = !!makoSession && !!(makoSession as IMkAuthenticatedSession).authenticatedUser
    const isAuthorized =
      !!makoSession &&
      !!(makoSession as IMkAuthorizedSession).authorizedUser &&
      !!(makoSession as IMkAuthorizedSession).memberAccessToken &&
      !!(makoSession as IMkAuthorizedSession).jwtToken

    // Check for persisted lockout state
    const persistedLockout = DataStore.getItem<ILockoutState>(EMkStorageKeys.MAKO_ACCOUNT_LOCKOUT, {
      store: localStorage,
      defaultValue: null,
    })

    // If lockout has expired, clear it
    let lockoutState: ILockoutState = {
      isAccountLocked: false,
      lockoutEndTime: null,
      lockedUsername: null,
    }

    if (persistedLockout && persistedLockout.isAccountLocked && persistedLockout.lockoutEndTime) {
      const now = Date.now()
      if (now < persistedLockout.lockoutEndTime) {
        // Lockout is still active
        lockoutState = {
          isAccountLocked: true,
          lockoutEndTime: persistedLockout.lockoutEndTime,
          lockedUsername: persistedLockout.lockedUsername,
        }
      } else {
        // Lockout has expired, clear it
        DataStore.removeItem(EMkStorageKeys.MAKO_ACCOUNT_LOCKOUT, { store: localStorage })
      }
    }

    const commonState = {
      error: undefined,
      isAuthenticated,
      isAuthorized,
      isAuthInitialized: false,
      isLoggingOut: false,
      loginOrigin,
      failedLoginAttempts: 0,
      failedMfaAttempts: 0,
      isAccountLocked: lockoutState.isAccountLocked,
      isMfaLocked: false,
      lockoutEndTime: lockoutState.lockoutEndTime,
      mfaLockoutEndTime: null,
      lockedUsername: lockoutState.lockedUsername,
    }
    if (isAuthorized === false && isAuthenticated === true) {
      return {
        ...commonState,
        session: makoSession as IMkAuthenticatedSession,
      }
    }
    if (isAuthorized === true) {
      return {
        ...commonState,
        session: makoSession as IMkAuthorizedSession,
      }
    }
    return {
      ...commonState,
      session: undefined,
    }
  }
  public abstract getContextProps<T>(): T

  public abstract login({ username, password }: { username: string; password: string }): Promise<void>

  public abstract loginWithToken(token: string, isActivation: boolean): Promise<OktaAuthnResponse>

  public abstract logout(hard?: boolean): Promise<void>

  public abstract onLoginCallback(onAuthorizedSession: (session: IMkAuthorizedSession) => void, onFailedToAuthorize: (error?: Error) => void): Promise<void>

  public abstract refreshSession?(): Promise<void>

  public abstract renewSession(): Promise<void>

  public abstract switchMembership(memberNumber: string): Promise<void>

  public abstract refreshMakoAuthorizationTokens?(): Promise<void>

  public abstract enrollMfaFactor(params: { stateToken: string; factorType: string; email?: string; phoneNumber?: string }): Promise<{
    factorId: string
    stateToken: string
    verifyLink?: string
    resendLink?: string
    /**
     * Indicates whether the MFA enrollment flow requires activation (e.g., Okta status MFA_ENROLL_ACTIVATE).
     * If true, downstream logic should use the "confirm" endpoint instead of "verify".
     */
    isActivationFlow?: boolean
  }>

  public abstract updateState(newState: Partial<TMkAuthenticationState>): void

  // public abstract onLogoutCallback(): Promise<void>

  // public abstract onRegisterCallback(): Promise<void>

  public static restoreOriginalUriBuilder = (navigate: (to: To, options?: { replace?: boolean }) => void) => async (_oktaAuth: any, originalUri: any) => {
    // Don't navigate during OAuth callback processing to prevent redirect to /login
    // The LoginCallback component will handle navigation after authorization completes
    if (window.location.pathname.includes('/account/login/callback')) {
      return
    }

    const loginOrigin = DataStore.getItem<string>(EMkStorageKeys.LOGIN_ORIGIN)
    let urlToUse = originalUri || '/'
    if (loginOrigin) {
      urlToUse = !['', '/', urlToUse].includes(loginOrigin) ? loginOrigin : urlToUse
    }
    const relativeUrl = toRelativeUrl(urlToUse, window.location)
    const routerPath = toRouterPath(relativeUrl)

    routeLogger.log({
      type: 'redirect',
      from: window.location.pathname,
      to: routerPath,
      method: 'restoreOriginalUri',
    })

    navigate(routerPath, { replace: true })
  }
}
